import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Mail01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { useAuth } from '../contexts/AuthContext';
import { FLAGS } from '../config/flags';
import { api } from '../utils/api';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const { login } = useAuth();

  const email = searchParams.get('email') || '';

  const handleOtpChange = (index, value) => {
    if (/^\d$/.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.some(digit => !digit)) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    const otpCode = otp.join('');
    setLoading(true);
    setError('');
    setSuccess(false);
    setSuccessMessage('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      const mockUser = { id: 'usr_mock123', email, role: email.includes('agent') ? 'agent' : 'client', email_verified: true, deriv_linked: false };
      login(mockUser, 'mock-access-token-' + Math.random(), false);
      setSuccess(true);
      setTimeout(() => navigate('/'), 1500);
      setLoading(false);
      return;
    }

    try {
      // Use documented endpoint /verify-email with payload { email, otp }
      const response = await api.post('/verify-email', { email, otp: otpCode });
      if (response.data && response.data.access_token) {
        login(response.data.user, response.data.access_token, false);
      }
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (loading || resendCooldown > 0) return;
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 500));
      setResendCooldown(60);
      setSuccessMessage('A mock verification code has been sent to your email.');
      setLoading(false);
      return;
    }

    try {
      // Use documented endpoint /resend-verification
      const response = await api.post('/resend-verification', { email });
      setResendCooldown(60);
      setSuccessMessage(response.message || 'A new verification code has been sent to your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        <div className="text-center">
          <div className="bg-soft-green/10 p-4 rounded-full inline-block mb-4">
            <HugeiconsIcon icon={Mail01Icon} size={32} className="text-soft-green" />
          </div>
          <h2 className="text-3xl font-bold text-charcoal dark:text-white">Verify Your Email</h2>
<p className="text-muted-gray text-lg max-w-md mx-auto mt-2">We&apos;ve sent a code to <span className="text-charcoal dark:text-white font-bold">{email}</span></p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-4 text-center">
                Verification Code
              </label>
              <div className="flex justify-center space-x-2 sm:space-x-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-10 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50 transition-all"
                  />
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-200/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl text-sm text-center">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 dark:bg-green-900/20 border border-green-200/50 text-green-700 dark:text-green-300 px-4 py-3 rounded-2xl text-sm text-center">
              Email verified successfully! Redirecting...
            </div>
          )}

          {successMessage && (
            <div className="bg-blue-100 dark:bg-blue-900/20 border border-blue-200/50 text-blue-700 dark:text-blue-300 px-4 py-3 rounded-2xl text-sm text-center">
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify Email'}
            <span className="absolute right-4 flex items-center">
              <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
            </span>
          </button>
        </form>

        <div className="text-center pt-4">
          <p className="text-sm text-muted-gray">
            Didn't receive the code?{' '}
            <button
              onClick={handleResendCode}
              disabled={loading || resendCooldown > 0}
              className="font-bold text-soft-green hover:text-light-mint disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
            </button>
          </p>
        </div>

        <div className="text-center">
          <p className="text-sm text-muted-gray">
            Need to use a different email?{' '}
            <span 
              className="font-bold text-soft-green hover:text-light-mint cursor-pointer transition-colors"
              onClick={() => navigate('/login')}
            >
              Go back to login
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
